import { useTranslation } from 'react-i18next'
import { Input } from '@/components/shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { HelpLabel } from '@/components/common/help/HelpLabel'
import { HelpText } from '@/components/common/help/HelpText'
import { helpKey } from '@/lib/action-modules/help'
import { ScheduleWeeksDefinitionEditor, type ScheduleWeeksDefinitionEditorProps } from './ScheduleWeeksDefinitionEditor'
import { ScheduleMonthMode, type ScheduleMonth } from '@/lib/action-modules/registry/scheduling/types'

export interface ScheduleMonthsDefinitionEditorProps extends ScheduleWeeksDefinitionEditorProps {
  monthModel: ScheduleMonth
  onUpdateMonthModel: (value: ScheduleMonth) => void
}

export function ScheduleMonthsDefinitionEditor(props: ScheduleMonthsDefinitionEditorProps) {
  const { t } = useTranslation('action-modules-wizard')
  const md = (key: string) => t(`schedulingStrategy.scheduleDefinition.months.${key}`)
  return (
    <div className="flex flex-col gap-2 pt-4">
      <h4 className="text-sm font-semibold">{md('title')}</h4>
      <HelpText helpKey={helpKey('schedulingMonths')} />
      <div className="flex flex-wrap items-end gap-3 pt-1">
        <div className="flex min-w-60 flex-1 flex-col gap-2">
          <HelpLabel helpKey={helpKey('schedulingMonthsScheduleMode')}>{md('scheduleMode.name')}</HelpLabel>
          <Select
            value={props.monthModel.mode}
            onValueChange={v => props.onUpdateMonthModel({ ...props.monthModel, mode: v as ScheduleMonthMode })}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ScheduleMonthMode.None}>{md('scheduleMode.none')}</SelectItem>
              <SelectItem value={ScheduleMonthMode.Repeat}>{md('scheduleMode.repeat')}</SelectItem>
              <SelectItem value={ScheduleMonthMode.Skip}>{md('scheduleMode.skip')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {props.monthModel.mode === ScheduleMonthMode.Skip && (
          <div className="flex flex-1 flex-col gap-2">
            <HelpLabel helpKey={helpKey('schedulingMonthsScheduleModeValue')}>{md('scheduleMode.value')}</HelpLabel>
            <Input
              type="number"
              value={props.monthModel.value}
              onChange={e => props.onUpdateMonthModel({ ...props.monthModel, value: Number(e.target.value) })}
            />
          </div>
        )}
      </div>
      <div className="flex max-w-xs flex-col gap-2">
        <HelpLabel helpKey={helpKey('schedulingMonthsOffset')}>{md('offset')}</HelpLabel>
        <Input
          type="number"
          value={props.monthModel.weekOffsetInsideMonth}
          onChange={e =>
            props.onUpdateMonthModel({ ...props.monthModel, weekOffsetInsideMonth: Number(e.target.value) })
          }
        />
      </div>
      <ScheduleWeeksDefinitionEditor
        weekModel={props.weekModel}
        onUpdateWeekModel={props.onUpdateWeekModel}
        dayModel={props.dayModel}
        onUpdateDayModel={props.onUpdateDayModel}
      />
    </div>
  )
}
